import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { detectPlatform, markOnboardingDone, onboardingDone } from '../lib/platform.js';
import {
  pushFeatureState,
  getPermissionState,
  getCurrentSubscription,
  subscribePush,
  sendTestPush,
} from '../lib/push.js';
import { useTrip } from '../lib/tripContext.jsx';
import { fetchSession } from '../lib/briefingApi.js';

// Standalone post-briefing landing. Walks players through:
//   1. Install to home screen (iOS or Android instructions)
//   2. Open from the home-screen icon (this page detects standalone)
//   3. Enable push notifications
//   4. Tap "Enter app" → role-aware home (/spectator for Anar, /wilman
//      for players)
//
// After completion the localStorage flag suppresses this page on
// future visits — if it ever re-renders (uninstalled / new device),
// we walk the user through again.

export default function Onboarding() {
  const navigate = useNavigate();
  const { tripId } = useTrip();
  const platform = detectPlatform();
  const [session, setSession] = useState({ loading: true });
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [testSent, setTestSent] = useState(null);

  // Fetch session directly. The app-wide SessionProvider was created
  // before the cookie was set (verify just happened), so its cached
  // state is stale. We need the fresh role to know where to send the
  // user when they tap "enter the app".
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [s, perm, sub] = await Promise.all([
        fetchSession({ tripId }).catch(() => ({ ok: false })),
        getPermissionState(),
        getCurrentSubscription().catch(() => null),
      ]);
      if (cancelled) return;
      setSession({ loading: false, ...s });
      setPermission(perm);
      setSubscribed(!!sub);
    })();
    return () => {
      cancelled = true;
    };
  }, [tripId]);

  // Already completed — skip straight to the role's home.
  if (!session.loading && onboardingDone()) {
    // Players land on /today (smart recommender) rather than /wilman.
    // Wilman is empty pre-trip; Today gives them weather windows and
    // trail context to chew on while they wait. Spectators still go
    // to /spectator (their dashboard).
    const home = session.role === 'spectator-judge' ? '/spectator' : '/today';
    return <Navigate to={home} replace />;
  }

  function enterApp() {
    markOnboardingDone();
    // Players land on /today (smart recommender) rather than /wilman.
    // Wilman is empty pre-trip; Today gives them weather windows and
    // trail context to chew on while they wait. Spectators still go
    // to /spectator (their dashboard).
    const home = session.role === 'spectator-judge' ? '/spectator' : '/today';
    navigate(home, { replace: true });
  }

  async function enablePush() {
    setError(null);
    setBusy('subscribe');
    try {
      await subscribePush({ tripId });
      setSubscribed(true);
      setPermission('granted');
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(null);
    }
  }

  async function testPush() {
    setError(null);
    setTestSent(null);
    setBusy('test');
    try {
      const r = await sendTestPush({ tripId });
      setTestSent(r);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(null);
    }
  }

  // ---------- Determine current step ----------
  // Standalone mode (display-mode: standalone OR navigator.standalone === true)
  // is the universal "installed" signal across iOS Safari + Android Chrome
  // + desktop Chrome. The earlier iOS-only gate left Android Chrome
  // browser-tab users falsely seeing "installed" — fixed.
  const pushFeat = pushFeatureState();
  const needsInstall = !platform.standalone;
  const canSubscribe = pushFeat.supported && permission !== 'denied';

  return (
    <div className="min-h-screen bg-bg-primary p-6 pb-12">
      <div className="mx-auto max-w-md space-y-6">
        <header>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
            assignment accepted · setup
          </p>
          <h1 className="mt-1 font-serif text-2xl">Install before the show</h1>
          <p className="mt-2 text-fg-secondary">
            Two quick steps. After this you won&apos;t see them again.
          </p>
        </header>

        {needsInstall ? (
          platform.isIOS ? <InstallStepsIOS /> : <InstallStepsAndroid />
        ) : (
          <InstalledConfirmed />
        )}

        <PushStep
          available={canSubscribe}
          subscribed={subscribed}
          permission={permission}
          busy={busy}
          standalone={platform.standalone}
          isIOS={platform.isIOS}
          isAndroid={platform.isAndroid}
          onEnable={enablePush}
          onTest={testPush}
          testSent={testSent}
        />

        {error && (
          <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={enterApp}
          className="w-full rounded-md border border-action bg-action px-4 py-3 font-mono text-sm uppercase tracking-[0.18em] text-action-fg transition hover:bg-action-hover"
        >
          enter the app
        </button>

        <footer className="pt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
          {session.role === 'spectator-judge' ? 'senior producer' : 'field operative'}
          {session.recipient_id ? ` · ${session.recipient_id}` : ''}
        </footer>
      </div>
    </div>
  );
}

// ---------- Step components ----------

function StepCard({ index, title, complete, children }) {
  return (
    <article
      className={[
        'card overflow-hidden border-l-4',
        complete ? 'border-l-success' : 'border-l-copper',
      ].join(' ')}
    >
      <header className="flex items-center justify-between border-b border-border-tertiary bg-bg-secondary px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-fg-tertiary">
        <span>step {index} · {title}</span>
        {complete ? (
          <span className="rounded-full border border-success/40 bg-success/10 px-2 py-0.5 text-success">
            done
          </span>
        ) : null}
      </header>
      <div className="space-y-3 px-4 py-4 text-sm text-fg-primary">{children}</div>
    </article>
  );
}

function InstallStepsIOS() {
  return (
    <StepCard index={1} title="add to home screen · iOS">
      <ol className="space-y-2 text-fg-secondary">
        <li>
          <span className="font-mono text-[11px] uppercase tracking-wider text-fg-tertiary">1.</span>{' '}
          In Safari, tap the <strong>Share</strong> icon (square with up-arrow) at the bottom.
        </li>
        <li>
          <span className="font-mono text-[11px] uppercase tracking-wider text-fg-tertiary">2.</span>{' '}
          Scroll the share sheet and tap <strong>Add to Home Screen</strong>.
        </li>
        <li>
          <span className="font-mono text-[11px] uppercase tracking-wider text-fg-tertiary">3.</span>{' '}
          Tap <strong>Add</strong>. The Some Say icon lands on your home screen.
        </li>
        <li>
          <span className="font-mono text-[11px] uppercase tracking-wider text-fg-tertiary">4.</span>{' '}
          Open the app from the <strong>home-screen icon</strong> (not Safari) and return here.
        </li>
      </ol>
      <p className="mt-2 rounded-md bg-bg-secondary px-3 py-2 font-mono text-[11px] text-fg-tertiary">
        push notifications on iOS only work from the installed PWA, not Safari. This is Apple&apos;s rule, not ours.
      </p>
    </StepCard>
  );
}

function InstallStepsAndroid() {
  return (
    <StepCard index={1} title="install app · Android">
      <ol className="space-y-2 text-fg-secondary">
        <li>
          <span className="font-mono text-[11px] uppercase tracking-wider text-fg-tertiary">1.</span>{' '}
          In Chrome, tap the <strong>three-dot menu</strong> (top right).
        </li>
        <li>
          <span className="font-mono text-[11px] uppercase tracking-wider text-fg-tertiary">2.</span>{' '}
          Tap <strong>Install app</strong> (or <strong>Add to Home Screen</strong>, depending on Chrome version).
        </li>
        <li>
          <span className="font-mono text-[11px] uppercase tracking-wider text-fg-tertiary">3.</span>{' '}
          Confirm. The Some Say icon lands on your home screen.
        </li>
        <li>
          <span className="font-mono text-[11px] uppercase tracking-wider text-fg-tertiary">4.</span>{' '}
          Open from the <strong>home-screen icon</strong> and return here.
        </li>
      </ol>
    </StepCard>
  );
}

function InstalledConfirmed() {
  return (
    <StepCard index={1} title="installed" complete>
      <p className="font-serif italic text-fg-primary">
        Running from the home-screen icon. Wilman acknowledges the effort.
      </p>
    </StepCard>
  );
}

function PushStep({ available, subscribed, permission, busy, standalone, isIOS, isAndroid, onEnable, onTest, testSent }) {
  // Both iOS Safari and Android Chrome users should install before
  // subscribing. iOS technically REQUIRES standalone (Apple gates
  // PushManager); Android Chrome allows browser-tab subscribe but it
  // defeats the home-screen icon flow — pushes would land against a
  // tab the user closes. Same gate, both platforms.
  if ((isIOS || isAndroid) && !standalone) {
    return (
      <StepCard index={2} title="enable transmissions">
        <p className="text-fg-secondary">
          Push notifications activate after step 1. Once you&apos;ve installed and reopened from the
          home-screen icon, this card unlocks.
        </p>
      </StepCard>
    );
  }

  if (!available) {
    return (
      <StepCard index={2} title="enable transmissions">
        <p className="text-fg-secondary">
          {permission === 'denied'
            ? 'Notifications are blocked for this site. Re-enable in your browser settings, then return.'
            : "Your browser doesn't support web push. Open this on iPhone or Android Chrome."}
        </p>
      </StepCard>
    );
  }

  if (subscribed) {
    return (
      <StepCard index={2} title="enable transmissions" complete>
        <p className="text-fg-secondary">
          Subscribed. Mr. Wilman will reach you when there&apos;s something to issue.
        </p>
        <button
          type="button"
          onClick={onTest}
          disabled={busy === 'test'}
          className="self-start rounded-md border border-border-tertiary bg-bg-secondary px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-fg-secondary transition hover:bg-bg-tertiary disabled:opacity-60"
        >
          {busy === 'test' ? 'sending…' : 'send test push'}
        </button>
        {testSent && (
          <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            test sent · {testSent.sent} delivered
            {testSent.removed ? ` · ${testSent.removed} dead removed` : ''}
          </p>
        )}
      </StepCard>
    );
  }

  return (
    <StepCard index={2} title="enable transmissions">
      <p className="text-fg-secondary">
        Wilman issues challenges, verdicts, and a daily brief through push notifications. Quiet
        hours are 9 PM to 9 AM Juneau time, hard-enforced.
      </p>
      <button
        type="button"
        onClick={onEnable}
        disabled={busy === 'subscribe'}
        className="self-start rounded-md border border-action bg-action px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-action-fg transition hover:bg-action-hover disabled:opacity-60"
      >
        {busy === 'subscribe' ? 'enabling…' : 'enable transmissions'}
      </button>
    </StepCard>
  );
}
